import React, { useState, useEffect, useCallback, useRef } from "react";
import clsx from "clsx";
import { Dialog, DialogContent } from "@mui/material";
import { TextField, Box } from "@curves/ui-base";
import { BarcodeScanner } from "@icoach/components/barCode";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import { isFunction } from "@util/utils";
import useGlobalBarcodeScanner from '@util/hook/useGlobalBarcodeScanner';

/**
 * 條碼掃描彈窗組件
 * 封裝條碼掃描器，並提供手動輸入選項
 *
 * @param open - 是否開啟彈窗
 * @param onClose - 關閉彈窗回調
 * @param onScanSuccess - 掃描成功回調
 * @param onScanError - 掃描錯誤回調
 * @param title - 彈窗標題
 * @param supportedFormats - 支援的條碼格式
 * @param allowManualInput - 是否允許手動輸入
 * @param placeholder - 手動輸入框佔位符
 * @param continuousScan - 是否連續掃描模式
 * @returns {JSX.Element}
 * @constructor
 */
const BarcodeScannerDialog = ({
	open = false,
	onClose: onCloseProps,
	onScanSuccess,
	onScanError = () => {},
	title = "條碼掃描",
	supportedFormats,
	allowManualInput = true,
	placeholder = "請掃描或輸入條碼",
	continuousScan = false,
}) => {
	const [isBarCodeClosing, setIsBarCodeClosing] = useState(false);
	const [manualInput, setManualInput] = useState("");

	const timeoutRef = useRef(null);
	const textFieldRef = useRef(null);
	const processedCodeRef = useRef(null); // 追蹤已處理的掃描碼，避免重複處理
	const processedCodeTimeoutRef = useRef(null); // 用於延遲重置 processedCodeRef（連續掃描模式）

	// 在 BarcodeScannerDialog 中處理實體掃描機
	const { lastScannedCode, resetScannedCode } = useGlobalBarcodeScanner({
		targetRef: textFieldRef,
		threshold: 500,
		terminatorKey: "Enter",
		enabled: open && !isBarCodeClosing, // 只在彈窗開啟且未關閉時啟用
	});

	// 處理掃描器成功或手動輸入提交
	const handleSuccess = useCallback(
		async decodedText => {
			if (isBarCodeClosing) return;

			// 將成功結果傳給外部回調，並等待結果
			let shouldClose = true;
			if (isFunction(onScanSuccess)) {
				const result = await onScanSuccess(decodedText);
				// 如果 onScanSuccess 返回 false，表示處理失敗，不關閉對話框
				if (result === false) {
					shouldClose = false;
				}
			}

			// 掃描成功後，清除手動輸入框內容
			setManualInput("");

			// 只有在成功且不是連續掃描模式時，才關閉彈窗
			if (shouldClose && !continuousScan) {
				onCloseProps();
			}
		},
		[isBarCodeClosing, onScanSuccess, continuousScan, onCloseProps]
	);

	// 監聽實體掃描機輸入
	useEffect(
		() => {
			if (lastScannedCode && open && !isBarCodeClosing) {
				// 避免重複處理相同的條碼（但在連續掃描模式下，允許在一定時間後重新處理）
				if (processedCodeRef.current === lastScannedCode) {
					return;
				}

				// 清除任何現有的重置計時器
				if (processedCodeTimeoutRef.current) {
					clearTimeout(processedCodeTimeoutRef.current);
					processedCodeTimeoutRef.current = null;
				}

				// 標記此掃描碼已被處理
				processedCodeRef.current = lastScannedCode;

				// 處理掃描成功
				handleSuccess(lastScannedCode);

				// 重置掃描碼狀態
				resetScannedCode();

				// 連續掃描模式下，設置一個延遲重置，允許重新掃描相同條碼
				if (continuousScan) {
					processedCodeTimeoutRef.current = setTimeout(() => {
						processedCodeRef.current = null;
						processedCodeTimeoutRef.current = null;
					}, 500); // 500ms 後允許重新掃描相同條碼
				}
			}
		},
		// eslint-disable-next-line
		[lastScannedCode, open, isBarCodeClosing, handleSuccess, resetScannedCode, continuousScan]
	);

	// 手動輸入值變更或掃描器輸入
	const handleManualInputChange = useCallback(
		e => {
			setManualInput(e.target.value);
		},
		// eslint-disable-next-line
		[]
	);

	// 處理手動輸入的提交（按下 Enter 鍵）
	const handleManualInputSubmit = useCallback(
		e => {
			if (e.key === "Enter") {
				const trimmedInput = manualInput.trim();
				if (trimmedInput) {
					handleSuccess(trimmedInput);
				}
				e.preventDefault();
				e.stopPropagation(); // 阻止事件冒泡，避免觸發全局掃描器
			}
		},
		[manualInput, handleSuccess]
	);

	const handleClose = useCallback(
		() => {
			// 1. 清除任何正在運行的舊計時器
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			setIsBarCodeClosing(true);
			onCloseProps();

			// 2. 啟動延遲重置，並將 ID 存入 ref，避免記憶體洩漏風險
			timeoutRef.current = setTimeout(() => {
				setIsBarCodeClosing(false);
				timeoutRef.current = null; // 重置 ref 標記
			}, 500);
		},
		// eslint-disable-next-line
		[onCloseProps]
	);

	// 確保元件卸載時清除計時器
	useEffect(
		() => {
			return () => {
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}
				if (processedCodeTimeoutRef.current) {
					clearTimeout(processedCodeTimeoutRef.current);
				}
			};
		},
		// eslint-disable-next-line
		[]
	);

	// 當彈窗狀態改變時，統一重置所有狀態
	useEffect(
		() => {
			if (!open) {
				// 確保在外部控制關閉時，延遲重置也被取消
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
					timeoutRef.current = null;
				}
				if (processedCodeTimeoutRef.current) {
					clearTimeout(processedCodeTimeoutRef.current);
					processedCodeTimeoutRef.current = null;
				}
				setManualInput("");
				setIsBarCodeClosing(false);
				processedCodeRef.current = null; // 重置已處理的掃描碼
				resetScannedCode(); // 重置掃描碼
			} else {
				// 彈窗開啟時，重置已處理的掃描碼追蹤
				processedCodeRef.current = null;
				if (processedCodeTimeoutRef.current) {
					clearTimeout(processedCodeTimeoutRef.current);
					processedCodeTimeoutRef.current = null;
				}
			}
		},
		// eslint-disable-next-line
		[open, resetScannedCode]
	);

	return (
		<Dialog className={clsx("simple-content", "dialog")} PaperProps={{ className: "wx-65rem" }} open={open} fullWidth>
			<GradientColorHeader onClose={handleClose}>{title}</GradientColorHeader>
			<DialogContent className="p-4">
				{allowManualInput && (
					<Box style={{ maxWidth: "500px" }} className={"mx-auto mb-3"}>
						<TextField
							ref={textFieldRef} // 添加 ref 以接收實體掃描機輸入
							placeholder={placeholder}
							value={manualInput}
							onChange={handleManualInputChange}
							onKeyDown={handleManualInputSubmit} // 監聽 Enter 鍵提交
							fullWidth
							autoFocus // 自動聚焦，方便掃描機輸入
						/>
					</Box>
				)}

				{open && !isBarCodeClosing && (
					<BarcodeScanner onScanSuccess={handleSuccess} onScanError={onScanError} title={title} supportedFormats={supportedFormats} hasButton continuousScan={continuousScan} />
				)}
			</DialogContent>
		</Dialog>
	);
};

export default BarcodeScannerDialog;
